import React, { useMemo, useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/cycle-api';

import { useOverlayState } from '../../../../hooks/useOverlayState';
import { useAbortController } from '../../../../hooks/useAbortController';
import { useSwrRequestKeysToMutateFromContext } from '../../../../hooks/useSwrRequestKeysToMutateFromContext';

import { mutateListDataAfterUpdatingItem } from '../../../../helpers/swrMutateUtils';

import { defaultErrorMessage } from '../../../../constants/errorMessages';

import StageCard from '../StageCard/StageCard';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import NewStageModal from '../NewStageModal/NewStageModal';
import SectionContainer from '../../PlaybookDetailPage/SectionContainer/SectionContainer';
import { Button } from '../../../../design-system';

const StagesTabSection = ({ cycleDetail, setCycleDetail }) => {
    const { id, current_stage_id, stages } = cycleDetail;

    const [errorAlert, setErrorAlert] = useState(null);

    const {
        abortRequest: abortGetRequest,
        getSignal: getSignalForGetRequest,
        isAbortError,
    } = useAbortController();
    const { abortRequest: abortPatchRequest, getSignal: getSignalForPatchRequest } =
        useAbortController();

    const {
        requestKeysToMutate: { cycles: requestKeysToMutate },
    } = useSwrRequestKeysToMutateFromContext();

    const { isOpened, open: openModal, close: closeModal } = useOverlayState();

    const activeStagesCount = useMemo(
        () => stages?.filter((stage) => !stage.is_deleted)?.length || 0,
        [stages]
    );

    const setStages = (cb) => {
        setCycleDetail((prevData) => {
            const updatedStages = typeof cb === 'function' ? cb([...(prevData.stages || [])]) : cb;
            return { ...prevData, stages: updatedStages };
        });
    };

    const makeStageDefault = async (stageId) => {
        try {
            setCycleDetail((data) => ({ ...data, current_stage_id: stageId }));

            abortGetRequest();
            abortPatchRequest();

            const patchSignal = getSignalForPatchRequest();
            const getSignal = getSignalForGetRequest();

            await client.patch(
                API.ROUTES.cycle.cycle + id + '/',
                { current_stage_id: stageId },
                { signal: patchSignal }
            );

            const { data } = await client.get(API.ROUTES.cycle.cycle + id + '/', {
                signal: getSignal,
            });

            const updatedPart = {
                current_stage: data.current_stage,
                current_stage_id: data.current_stage_id,
            };

            setCycleDetail((prevData) => ({ ...prevData, ...updatedPart }));

            void mutateListDataAfterUpdatingItem({ requestKeysToMutate, id, updatedPart });
        } catch (e) {
            if (isAbortError(e)) return;

            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        }
    };

    const renderButton = () => {
        return (
            <div className="min-w-fit">
                <Button type="link" size="sm" text="New Stage" underlined onClick={openModal} />
            </div>
        );
    };

    return (
        <SectionContainer
            title="Cycle Stages"
            description="Configure, submit, and review each stage of the CRAFT cycle for this solution "
            variant="secondary"
            marginClassName="mb-5"
            paddingClassName="p-4 sm:p-5"
            renderButton={renderButton}
        >
            <div className="flex flex-col gap-2.5">
                {stages?.map((stage) => {
                    const isDefault = stage.id === current_stage_id;
                    const isDeleteAvailable = !isDefault && activeStagesCount > 1;

                    return (
                        <StageCard
                            key={stage.id}
                            stage={stage}
                            setStages={setStages}
                            isDefault={isDefault}
                            isDeleteAvailable={isDeleteAvailable}
                            makeStageDefault={makeStageDefault}
                            cycleId={id}
                        />
                    );
                })}
            </div>

            {isOpened && <NewStageModal cycleId={id} setStages={setStages} onClose={closeModal} />}

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </SectionContainer>
    );
};

export default StagesTabSection;
